(() => {
  if (window.__depotCollectionLoaded) return;
  window.__depotCollectionLoaded = true;

  const shopRoot = window.Shopify?.routes?.root || '/';
  const cartUpdateUrl = `${shopRoot}cart/update.js`;

  let pendingSave = Promise.resolve();
  let savesInProgress = 0;
  let saveFailed = false;
  let replayingCheckout = false;
  let cleanupQueued = false;
  let observerScheduled = false;

  const getRoots = () =>
    Array.from(document.querySelectorAll('[data-depot-root]'));

  const getEligibleRoots = () =>
    getRoots().filter(
      (root) => root.dataset.depotEligible === 'true'
    );

  function readState(root) {
    const checkbox = root.querySelector('[data-depot-enabled]');
    const select = root.querySelector('[data-depot-select]');
    const selectedOption = select?.selectedOptions?.[0];

    return {
      enabled: Boolean(checkbox?.checked),
      name: select?.value || '',
      code: selectedOption?.dataset?.code || '',
      address: selectedOption?.dataset?.address || '',
      phone: selectedOption?.dataset?.phone || '',
      email: selectedOption?.dataset?.email || '',
      map: selectedOption?.dataset?.map || ''
    };
  }

  function setLink(link, text, href) {
    if (!link) return;

    link.textContent = text || '';
    link.href = href || '';
    link.hidden = !text || !href;
  }

  function applyState(root, state) {
    if (root.dataset.depotEligible !== 'true') return;

    const checkbox = root.querySelector('[data-depot-enabled]');
    const fields = root.querySelector('[data-depot-fields]');
    const select = root.querySelector('[data-depot-select]');
    const details = root.querySelector('[data-depot-details]');
    const address = root.querySelector('[data-depot-address]');
    const phoneLink = root.querySelector('[data-depot-phone-link]');
    const emailLink = root.querySelector('[data-depot-email-link]');
    const mapLink = root.querySelector('[data-depot-map-link]');
    const separator = root.querySelector(
      '[data-depot-contact-separator]'
    );
    const error = root.querySelector('[data-depot-error]');

    if (checkbox) checkbox.checked = state.enabled;
    if (fields) fields.hidden = !state.enabled;

    if (select) {
      select.disabled = !state.enabled;

      const optionExists = Array.from(select.options).some(
        (option) => option.value === state.name
      );

      if (state.name && optionExists) {
        select.value = state.name;
      } else if (!state.enabled) {
        select.value = '';
      }
    }

    if (address) address.textContent = state.address || '';

    setLink(
      phoneLink,
      state.phone,
      state.phone ? `tel:${state.phone.replace(/[^\d+]/g, '')}` : ''
    );

    setLink(
      emailLink,
      state.email,
      state.email ? `mailto:${state.email}` : ''
    );

    setLink(mapLink, 'View this depot on Google Maps', state.map);

    if (separator) {
      separator.hidden = !(state.phone && state.email);
    }

    if (details) {
      details.hidden =
        !state.enabled ||
        !state.name ||
        !(state.address || state.phone || state.email || state.map);
    }

    if (error) error.hidden = true;
  }

  function applyStateToAll(state) {
    getEligibleRoots().forEach((root) => applyState(root, state));
  }

  function showStatus(message, isError = false) {
    getRoots().forEach((root) => {
      const status = root.querySelector('[data-depot-status]');
      if (!status) return;

      status.textContent = message;
      status.classList.toggle(
        'depot-collection__status--error',
        isError
      );
    });
  }

  function showValidationError(message) {
    getEligibleRoots().forEach((root) => {
      const error = root.querySelector('[data-depot-error]');
      if (!error) return;

      error.textContent = message;
      error.hidden = false;
    });
  }

  function saveState(state) {
    savesInProgress += 1;
    showStatus('Saving depot selection…');

    const attributes = state.enabled
      ? {
          'Delivery preference': 'Depot collection',
          'Collection depot': state.name,
          'Collection depot code': state.code,
          'Collection depot address': state.address,
          'Collection depot phone': state.phone,
          'Collection depot email': state.email,
          'Collection depot map': state.map
        }
      : {
          'Delivery preference': '',
          'Collection depot': '',
          'Collection depot code': '',
          'Collection depot address': '',
          'Collection depot phone': '',
          'Collection depot email': '',
          'Collection depot map': ''
        };

    pendingSave = pendingSave
      .catch(() => null)
      .then(async () => {
        const response = await fetch(cartUpdateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ attributes })
        });

        if (!response.ok) {
          throw new Error(
            `Cart update failed with status ${response.status}`
          );
        }

        return response.json();
      })
      .then((cart) => {
        saveFailed = false;

        getRoots().forEach((root) => {
          root.dataset.currentPreference =
            cart?.attributes?.['Delivery preference'] || '';
        });

        showStatus(
          state.enabled
            ? state.name
              ? 'Collection depot saved.'
              : 'Select a depot to continue.'
            : ''
        );

        return cart;
      })
      .catch((error) => {
        saveFailed = true;
        console.error('Unable to save depot selection:', error);

        showStatus(
          'The depot selection could not be saved. Please try again.',
          true
        );

        return null;
      })
      .finally(() => {
        savesInProgress = Math.max(0, savesInProgress - 1);
      });

    return pendingSave;
  }

  function initialiseSelectors() {
    const eligibleRoots = getEligibleRoots();

    if (eligibleRoots.length > 0) {
      const state = readState(eligibleRoots[0]);
      applyStateToAll(state);
      cleanupQueued = false;
      return;
    }

    const hasStaleSelection = getRoots().some(
      (root) =>
        root.dataset.currentPreference === 'Depot collection'
    );

    if (hasStaleSelection && !cleanupQueued) {
      cleanupQueued = true;

      saveState({
        enabled: false,
        name: '',
        code: '',
        address: '',
        phone: '',
        email: '',
        map: ''
      }).finally(() => {
        cleanupQueued = false;
      });
    }
  }

  document.addEventListener('change', (event) => {
    const root = event.target.closest('[data-depot-root]');

    if (!root || root.dataset.depotEligible !== 'true') return;

    const isCheckbox = event.target.matches(
      '[data-depot-enabled]'
    );
    const isSelect = event.target.matches('[data-depot-select]');

    if (!isCheckbox && !isSelect) return;

    const state = readState(root);

    if (isCheckbox && !state.enabled) {
      state.name = '';
      state.code = '';
      state.address = '';
      state.phone = '';
      state.email = '';
      state.map = '';
    }

    applyStateToAll(state);
    saveState(state);
  });

  document.addEventListener(
    'click',
    (event) => {
      const checkoutControl = event.target.closest(
        [
          'button[name="checkout"]',
          'input[name="checkout"]',
          '#CartDrawer-Checkout',
          'a[href$="/checkout"]',
          'a[href*="/checkout?"]'
        ].join(',')
      );

      if (!checkoutControl || replayingCheckout) return;

      const activeRoot = getEligibleRoots()[0];

      if (activeRoot) {
        const state = readState(activeRoot);

        if (state.enabled && !state.name) {
          event.preventDefault();
          event.stopImmediatePropagation();

          showValidationError(
            'Please select a collection depot before proceeding to checkout.'
          );

          activeRoot
            .querySelector('[data-depot-select]')
            ?.focus();

          return;
        }
      }

      if (saveFailed) {
        event.preventDefault();
        event.stopImmediatePropagation();

        showValidationError(
          'The depot selection could not be saved. Please change the selection and try again.'
        );

        return;
      }

      if (savesInProgress > 0) {
        event.preventDefault();
        event.stopImmediatePropagation();

        pendingSave.finally(() => {
          if (saveFailed) {
            showValidationError(
              'The depot selection could not be saved. Please try again.'
            );
            return;
          }

          replayingCheckout = true;
          checkoutControl.click();

          window.setTimeout(() => {
            replayingCheckout = false;
          }, 0);
        });
      }
    },
    true
  );

  const observer = new MutationObserver(() => {
    if (observerScheduled) return;

    observerScheduled = true;

    window.requestAnimationFrame(() => {
      observerScheduled = false;
      initialiseSelectors();
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  initialiseSelectors();
})();
